<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { resetPassword } from '../../../../api/auth';

const route = useRoute();
const router = useRouter();

const RESEND_COOLDOWN_SECONDS = 30;

const status = ref('request');
const credentials = reactive({ email: '' });
const isLoading = ref(false);
const resendCooldown = ref(0);
let cooldownTimer = null;

const isEmailValid = computed(() =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(credentials.email)
);

const canSubmitRequest = computed(
  () => isEmailValid.value && !isLoading.value
);

const startCooldown = () => {
  resendCooldown.value = RESEND_COOLDOWN_SECONDS;
  if (cooldownTimer) clearInterval(cooldownTimer);
  cooldownTimer = setInterval(() => {
    resendCooldown.value -= 1;
    if (resendCooldown.value <= 0) {
      clearInterval(cooldownTimer);
      cooldownTimer = null;
    }
  }, 1000);
};

const submitRequest = async () => {
  if (!canSubmitRequest.value) return;
  isLoading.value = true;
  try {
    await resetPassword({ email: credentials.email });
    status.value = 'sent';
    startCooldown();
  } catch (error) {
    const msg =
      error?.response?.data?.message ||
      error?.message ||
      'Could not send the reset link. Please try again.';
    useAlert(msg);
  } finally {
    isLoading.value = false;
  }
};

const resendResetEmail = async () => {
  if (isLoading.value || resendCooldown.value > 0) return;
  isLoading.value = true;
  try {
    await resetPassword({ email: credentials.email });
    useAlert('Reset link re-sent — please check your inbox.');
    startCooldown();
  } catch {
    useAlert('Could not resend the reset link. Please try again.');
  } finally {
    isLoading.value = false;
  }
};

const changeEmail = () => {
  status.value = 'request';
};

const goBackToSignIn = () => {
  router.push({ path: '/app/login' });
};

onMounted(() => {
  if (route.query?.email) {
    credentials.email = decodeURIComponent(String(route.query.email));
  }
});

onBeforeUnmount(() => {
  if (cooldownTimer) clearInterval(cooldownTimer);
});
</script>

<template>
  <div class="cc-reset-page">
    <div class="cc-reset-noise" />

    <header class="cc-reset-header">
      <div class="cc-brand-mark">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="38"
          height="38"
          viewBox="0 0 96 92"
          class="cc-brand-icon"
        >
          <defs>
            <linearGradient id="goldgrad-rs" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#d8b86a" />
              <stop offset="0.5" stop-color="#a8842f" />
              <stop offset="1" stop-color="#7a5a14" />
            </linearGradient>
            <clipPath id="clipover-rs">
              <rect x="38" y="44" width="20" height="22" />
            </clipPath>
          </defs>
          <path
            d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 24 36 L 7 47 L 12 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
            transform="translate(7,11)"
            fill="none"
            stroke="#0d9488"
            stroke-width="7"
            stroke-linejoin="round"
          />
          <g transform="translate(7,11)" fill="#0d9488">
            <circle cx="15" cy="18" r="2.4" />
            <circle cx="24" cy="18" r="2.4" />
            <circle cx="33" cy="18" r="2.4" />
          </g>
          <path
            d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 32 36 L 37 47 L 20 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
            transform="translate(41,30)"
            fill="none"
            stroke="url(#goldgrad-rs)"
            stroke-width="7"
            stroke-linejoin="round"
          />
          <g transform="translate(41,30)" fill="#a8842f">
            <circle cx="15" cy="18" r="2.4" />
            <circle cx="24" cy="18" r="2.4" />
            <circle cx="33" cy="18" r="2.4" />
          </g>
          <g clip-path="url(#clipover-rs)">
            <path
              d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 24 36 L 7 47 L 12 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
              transform="translate(7,11)"
              fill="none"
              stroke="#0d9488"
              stroke-width="7"
              stroke-linejoin="round"
            />
          </g>
        </svg>
        <div class="cc-brand-text">
          <div class="cc-wordmark">
            <span class="cc-wordmark-part cc-wordmark-creator">Creator</span><span class="cc-wordmark-dash">-</span><span class="cc-wordmark-part cc-wordmark-chat">Chat</span><span class="cc-wordmark-tld">.com</span>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="90"
            height="2"
            viewBox="0 0 90 2"
            class="cc-brand-divider"
          >
            <defs>
              <linearGradient id="tealfade-rs" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#0d9488" stop-opacity="0" />
                <stop offset="20%" stop-color="#0d9488" stop-opacity="1" />
                <stop offset="80%" stop-color="#0d9488" stop-opacity="1" />
                <stop offset="100%" stop-color="#0d9488" stop-opacity="0" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="90" height="2" fill="url(#tealfade-rs)" />
          </svg>
          <div class="cc-tagline">Conversations · Tailored</div>
        </div>
      </div>
    </header>

    <main class="cc-reset-main">
      <!-- REQUEST -->
      <div v-if="status === 'request'" class="cc-auth-stack">
        <div class="cc-auth-card">
          <h1 class="cc-h-display">Forgot your password?</h1>
          <p class="cc-sub">
            Enter the email associated with your account and we'll send you a link to reset your password.
          </p>

          <form class="cc-form" @submit.prevent="submitRequest">
            <div class="cc-field">
              <label class="cc-label">
                Work email <span class="cc-req">*</span>
              </label>
              <input
                v-model="credentials.email"
                type="email"
                class="cc-input"
                placeholder="you@agency.com"
                autocomplete="username"
                autofocus
              />
            </div>

            <button
              type="submit"
              class="cc-btn-primary"
              :disabled="!canSubmitRequest"
            >
              <template v-if="isLoading">Sending reset link…</template>
              <template v-else>Send reset link</template>
            </button>
          </form>

          <div class="cc-info-panel">
            <strong>A note on security:</strong>
            For your protection, we don't reveal whether an account exists for any given email. If we find a match, you'll receive a reset link within a few minutes.
          </div>

          <div class="cc-card-foot">
            <a href="#" class="cc-link-accent" @click.prevent="goBackToSignIn">
              ← Back to sign in
            </a>
          </div>
        </div>
      </div>

      <!-- SENT -->
      <div v-else class="cc-auth-stack">
        <div class="cc-auth-card cc-auth-card--centered">
          <div class="cc-envelope-icon">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </div>
          <h1 class="cc-h-display">Check your email</h1>
          <p class="cc-sub">
            If an account exists for that email, we've sent a password reset link to
          </p>
          <p class="cc-email">{{ credentials.email }}</p>

          <div class="cc-tip-panel">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0f766e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="cc-tip-icon">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <div>
              The reset link expires in <strong>1 hour</strong>. If you don't see it within a few minutes, check your spam folder.
            </div>
          </div>

          <button
            type="button"
            class="cc-btn-primary"
            :disabled="isLoading || resendCooldown > 0"
            @click="resendResetEmail"
          >
            <template v-if="isLoading">Resending…</template>
            <template v-else-if="resendCooldown > 0">
              Resend available in {{ resendCooldown }}s
            </template>
            <template v-else>Resend email</template>
          </button>

          <div class="cc-card-foot cc-card-foot--row">
            <a href="#" class="cc-link-muted" @click.prevent="changeEmail">
              Use a different email
            </a>
            <a href="#" class="cc-link-accent" @click.prevent="goBackToSignIn">
              ← Back to sign in
            </a>
          </div>
        </div>
      </div>
    </main>

    <footer class="cc-site-footer" role="contentinfo">
      <div class="cc-site-footer-links">
        <a href="https://creator-chat.com/#features" target="_blank" rel="noopener">Features</a>
        <a href="https://creator-chat.com/#fangate" target="_blank" rel="noopener">Fangate</a>
        <a href="https://creator-chat.com/#pricing" target="_blank" rel="noopener">Pricing</a>
        <a href="https://creator-chat.com/faq.html" target="_blank" rel="noopener">FAQ</a>
        <a href="mailto:info@creator-chat.com">Contact</a>
        <a href="https://creator-chat.com/imprint.html" target="_blank" rel="noopener">Imprint</a>
        <a href="https://creator-chat.com/privacy.html" target="_blank" rel="noopener">Privacy</a>
        <a href="https://creator-chat.com/terms.html" target="_blank" rel="noopener">Terms</a>
      </div>
      <div class="cc-site-footer-meta">
        <div class="cc-site-footer-email">
          <a href="mailto:info@creator-chat.com">info@creator-chat.com</a>
        </div>
        © 2026 Creator Chat. Built for creators who take their work seriously. Hosted in the European Union.
      </div>
    </footer>
  </div>
</template>

<style scoped>
.cc-reset-page {
  position: relative;
  min-height: 100vh;
  width: 100%;
  background: #efeae0;
  color: #0b1414;
  font-family: 'Inter', sans-serif;
  display: flex;
  flex-direction: column;
}
.cc-reset-noise {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  opacity: 0.35;
  mix-blend-mode: multiply;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.4 0 0 0 0 0.35 0 0 0 0 0.25 0 0 0 0.05 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
}

.cc-reset-header {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px 36px;
}

.cc-brand-mark { display: flex; align-items: center; }
.cc-brand-icon { display: block; margin-right: 14px; }
.cc-brand-text { line-height: 1; }
.cc-wordmark { line-height: 21px; white-space: nowrap; }
.cc-wordmark-part {
  font-family: 'Fraunces', 'Times New Roman', Georgia, serif;
  font-size: 21px;
  letter-spacing: 1.26px;
  text-transform: uppercase;
  color: #0b1414;
}
.cc-wordmark-creator { font-weight: 400; }
.cc-wordmark-chat { font-weight: 300; }
.cc-wordmark-dash {
  font-family: 'Fraunces', 'Times New Roman', Georgia, serif;
  font-size: 21px;
  letter-spacing: 1.26px;
  color: #a8842f;
  font-weight: 400;
}
.cc-wordmark-tld {
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: lowercase;
  color: #a8842f;
  vertical-align: 8px;
  padding-left: 2px;
}
.cc-brand-divider { display: block; margin: 6px 0 5px 0; }
.cc-tagline {
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  font-size: 8.5px;
  font-weight: 500;
  letter-spacing: 2.56px;
  color: rgba(11, 20, 20, 0.5);
  text-transform: uppercase;
  line-height: 1;
}

.cc-reset-main {
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 24px 40px;
}

.cc-auth-stack {
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
}

.cc-auth-card {
  background: #ffffff;
  border: 1px solid #e0d8c5;
  border-radius: 16px;
  padding: 38px 36px 32px;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.5) inset,
    0 6px 24px rgba(11, 20, 20, 0.04);
}
.cc-auth-card--centered { text-align: center; }

.cc-h-display {
  font-family: 'Cinzel', 'Fraunces', 'Times New Roman', Georgia, serif;
  font-weight: 600;
  color: #0b1414;
  letter-spacing: -0.005em;
  font-size: 26px;
  line-height: 1.2;
  margin: 0 0 6px;
  text-align: center;
}
.cc-sub {
  font-family: 'Inter', sans-serif;
  font-size: 13.5px;
  color: #6e7570;
  line-height: 1.5;
  margin: 0 0 24px;
  text-align: center;
}

.cc-email {
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  color: #0b1414;
  font-weight: 600;
  letter-spacing: 0.06em;
  margin: 0 0 22px;
  word-break: break-all;
  text-align: center;
}

.cc-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.cc-field { display: flex; flex-direction: column; }
.cc-label {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: #2a3434;
  margin-bottom: 6px;
}
.cc-req { color: #dc2626; font-weight: 800; margin-left: 2px; }

.cc-input {
  width: 100%;
  box-sizing: border-box;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #0b1414;
  padding: 11px 14px;
  border-radius: 10px;
  border: 1.5px solid #e0d8c5;
  background: #fdfcf9;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.cc-input:hover { border-color: #cfc5ac; }
.cc-input:focus {
  border-color: #0d9488;
  box-shadow: 0 0 0 4px rgba(13, 148, 136, 0.22);
}

.cc-btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 12px 20px;
  border-radius: 10px;
  border: 1.5px solid #0b1414;
  background: #0b1414;
  color: #f5efe0;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: all 0.15s;
  width: 100%;
}
.cc-btn-primary:hover:not(:disabled) {
  background: #142020;
  border-color: #142020;
}
.cc-btn-primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.cc-info-panel {
  margin-top: 18px;
  padding: 12px 14px;
  background: #f1ede4;
  border-radius: 9px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #6e7570;
  line-height: 1.5;
}
.cc-info-panel strong { color: #2a3434; }

.cc-envelope-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 20px;
  border-radius: 18px;
  background: rgba(13, 148, 136, 0.22);
  color: #0f766e;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cc-tip-panel {
  background: rgba(13, 148, 136, 0.08);
  border: 1px solid rgba(13, 148, 136, 0.22);
  border-radius: 11px;
  padding: 14px 16px;
  text-align: left;
  margin-bottom: 22px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  color: #2a3434;
  line-height: 1.55;
}
.cc-tip-icon { flex-shrink: 0; margin-top: 1px; }

.cc-card-foot {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #e0d8c5;
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #6e7570;
}
.cc-card-foot--row {
  display: flex;
  justify-content: center;
  gap: 18px;
  font-size: 12.5px;
}
.cc-link-accent {
  color: #0f766e;
  font-weight: 600;
  text-decoration: none;
}
.cc-link-accent:hover { text-decoration: underline; }
.cc-link-muted {
  color: #6e7570;
  text-decoration: none;
  font-weight: 500;
}
.cc-link-muted:hover { color: #0f766e; text-decoration: underline; }

.cc-site-footer {
  position: relative;
  z-index: 1;
  border-top: 1px solid #e0d8c5;
  margin-top: 64px;
  padding: 40px 36px 36px;
}
.cc-site-footer-links {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1100px;
  margin: 0 auto;
}
.cc-site-footer-links a {
  color: #6e7570;
  text-decoration: none;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  transition: color 0.15s;
}
.cc-site-footer-links a:hover { color: #0f766e; }
.cc-site-footer-meta {
  color: #6e7570;
  font-size: 12px;
  margin: 24px auto 0;
  max-width: 1100px;
  text-align: center;
  padding-top: 24px;
  border-top: 1px solid #e0d8c5;
  font-family: 'Inter', sans-serif;
}
.cc-site-footer-meta a {
  color: inherit;
  text-decoration: none;
  font-weight: 600;
}
.cc-site-footer-email { margin-bottom: 6px; }

@media (max-width: 540px) {
  .cc-reset-header { padding: 18px 20px; }
  .cc-reset-main { padding: 16px 16px 32px; }
  .cc-auth-card { padding: 30px 22px 26px; }
  .cc-site-footer { padding: 32px 20px 28px; margin-top: 48px; }
  .cc-site-footer-links { gap: 16px; }
  .cc-tagline { display: none; }
}
</style>
